import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  TextField,
  Alert,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { Stack, fontSize } from '@mui/system';
import { white, darkBlue, darkBlue2 } from '@styles/colors';
import { FC } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import { useSendEnterpriseEmails } from '@features/enterprise/hooks/useSendEnterpriseEmails';
import { LoadingButton } from '@mui/lab';
import { useGetEnterpriseCodes } from '@features/applications/hooks/useGetEnterpriseCodes';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useInviteEnterpriseBulk } from '@features/enterprise/hooks/useInviteEnterpriseBulk';
import { InviteIcon } from '../../../../vectors/index';

type AddCompanyEnterpriseModalProps = {
  showAddComapaniesModal: Boolean;
  handleClose: () => void;
};

const AddCompanyEnterpriseModal: FC<AddCompanyEnterpriseModalProps> = ({
  showAddComapaniesModal,
  handleClose,
}) => {
  const [showEmailInputs, setShowEmailInputs] = useState(false);
  const [showShareableCodes, setShowSahereableCodes] = useState(false);
  const [emails, setEmails] = useState<string[]>(['']);
  const [invalidEmailIndexes, setInvalidEmailIndexes] = useState<number[]>([]);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [copiedCodes, setCopiedCodes] = useState<string[]>([]);
  const { sendEnterpriseEmails, isSendingEnterpriseEmails } =
    useSendEnterpriseEmails({
      setErrorText,
      setSuccessText,
      setShowEmailInputs,
    });
  const { inviteEnterpriseBulk, isInvitingEnterpriseBulk } =
    useInviteEnterpriseBulk({ setErrorText, setSuccessText });
  const { enterpriseCodesData } = useGetEnterpriseCodes();

  const handleAddEmailInput = () => {
    setEmails([...emails, '']);
  };

  const handleEmailInputChange = (index: number, value: string) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = value;
    setEmails(updatedEmails);
    setInvalidEmailIndexes([]);
  };

  const handleSendEmails = () => {
    const newInvalidEmailIndexes: number[] = [];
    emails.forEach((email, index) => {
      if (!isValidEmail(email)) {
        newInvalidEmailIndexes.push(index);
      }
    });
    setInvalidEmailIndexes(newInvalidEmailIndexes);
    if (newInvalidEmailIndexes.length === 0) {
      const formattedEmails = emails
        .filter((email) => email.trim() !== '')
        .join(', ');
      sendEnterpriseEmails({ email: formattedEmails });
    }
  };

  const handleShowCodes = () => {
    inviteEnterpriseBulk();
  };

  const handleCancel = () => {
    setShowEmailInputs(false);
    setShowSahereableCodes(false);
    setEmails(['']);
    setInvalidEmailIndexes([]);
    setSuccessText('');
    setErrorText('');
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCopyClick = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedCodes([...copiedCodes, text]);
        setTimeout(() => {
          setCopiedCodes(copiedCodes.filter((code) => code !== text));
        }, 1500);
      })
      .catch((error) => {
        console.error('Unable to copy to clipboard. Error:', error);
      });
  };

  const renderEmailInputs = () => {
    return emails.map((email, index) => (
      <TextField
        key={index}
        label="name@example.com"
        type="email"
        size="small"
        value={email}
        onChange={(e) => handleEmailInputChange(index, e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          },
          '& input': {
            fontSize: '14px',
            padding: '10px 15px',
          },
          ...(invalidEmailIndexes.includes(index) && {
            '& .MuiInput-root': { color: 'red' },
          }),
        }}
        error={invalidEmailIndexes.includes(index)}
        helperText={
          invalidEmailIndexes.includes(index) && 'Invalid email format'
        }
      />
    ));
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableEnforceFocus
    >

      <DialogTitle>
        <IconButton sx={{ position: 'absolute', top: 5, right: 5 }} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
        <br />
        <InviteIcon />
        <br />
        <Typography variant="h4" >
          Invite Company
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: white, p: 6 }}>
        {errorText && (
          <Alert
            severity="error"
            sx={{ marginBottom: '10px' }}
          >
            {errorText}
          </Alert>
        )}
        {successText && (
          <Alert
            severity="success"
            sx={{ marginBottom: '10px' }}
          >
            {successText}
          </Alert>
        )}
        <Grid>
          <Stack
            spacing={4}
            direction={{ sm: 'column' }}
            justifyContent="center"
            alignItems="center"
          >
            <Grid>
              <Stack
                direction={{ sm: 'column' }}
                justifyContent="center"
                alignItems="center"
              >
                <Typography sx={{ fontWeight: 'bold', mb: 1 }} variant="h6">
                  Add Compnaies to your Enterprise Account
                </Typography>
                {!showEmailInputs && !showShareableCodes && (
                  <Typography sx={{ mb: 2 }} fontSize="14px">
                    How would you like to invite companies?
                  </Typography>
                )}
              </Stack>
            </Grid>
            {!showEmailInputs && !showShareableCodes && (
              <Stack
                spacing={2}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent={{ sm: 'space-between' }}
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ color: darkBlue, border: `1px solid ${darkBlue}` }}
                  style={{
                    padding: '5px 15px',
                    textTransform: 'none',
                    fontSize: '14px',
                  }}
                  onClick={() => {
                    setShowEmailInputs(true);
                    setSuccessText('');
                    setErrorText('');
                  }}
                >
                  Send Email(s)
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: darkBlue }}
                  style={{
                    padding: '6px 15px',
                    fontSize: '14px',
                  }}
                  onClick={() => {
                    setShowSahereableCodes(true);
                    setSuccessText('');
                    setErrorText('');
                  }}
                >
                  Shareable Code(s)
                </Button>
              </Stack>
            )}
            {showEmailInputs && (
              <>
                {renderEmailInputs()}
                <Button
                  variant="text"
                  size="small"
                  sx={{ color: darkBlue }}
                  onClick={handleAddEmailInput}
                  startIcon={<AddCircleOutlinedIcon />}
                  style={{ fontSize: '14px' }}
                >
                  Add another
                </Button>
                <Stack
                  spacing={2}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent={{ sm: 'space-between' }}
                  alignItems="center"
                  style={{ marginTop: '30px' }}
                >
                  <LoadingButton
                    variant="contained"
                    size="small"
                    style={{ minWidth: '100px' }}
                    sx={{
                      bgcolor: darkBlue,
                      padding: '6px 15px',
                      fontSize: '14px',
                    }}
                    onClick={handleSendEmails}
                    loading={isSendingEnterpriseEmails}
                  >
                    Send
                  </LoadingButton>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      padding: '6px 15px',
                      fontSize: '14px',
                      minWidth: '100px',
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
            {showShareableCodes && (
              <>
                <Typography
                  fontSize="14px"
                  style={{
                    textAlign: 'center',
                    marginBottom: '0px',
                    marginTop: '2px',
                  }}
                >
                  You can directly share any of following bulk codes with
                  companies you want to be associated with your enterprise
                  account and then, they can enter the received code on their
                  account page to join your enterprise.
                </Typography>
                <Grid
                  container
                  spacing={{ xs: 2, md: 3 }}
                  columns={{ xs: 4, sm: 8, md: 12 }}
                  style={{ marginTop: '12px' }}
                >
                  {enterpriseCodesData &&
                    enterpriseCodesData.filter((item: { company: any; }) => !item.company).map((code: any, index: any) => (
                      <Grid
                        item
                        xs={2}
                        sm={4}
                        md={4}
                        key={index}
                        textAlign="center"
                      >
                        <Typography
                          variant="h6"
                          style={{ fontFamily: 'monospace' }}
                          position="relative"
                          fontSize="13px"
                        >
                          {code.short_code}
                          <ContentCopyIcon
                            sx={{
                              fontSize: '13px',
                              cursor: 'pointer',
                              marginLeft: '5px',
                              position: 'relative',
                              top: '1px',
                            }}
                            onClick={() => handleCopyClick(code.short_code)}
                          />
                          {copiedCodes.includes(code.short_code) && (
                            <Typography
                              variant="body2"
                              style={{
                                color: 'green',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                top: '-15px',
                                fontSize: '11px',
                                transition: 'all0.35s ease',
                              }}
                            >
                              Copied!
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                    ))}
                </Grid>
                <Stack
                  spacing={2}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent={{ sm: 'space-between' }}
                  alignItems="center"
                  style={{ marginTop: '30px' }}
                >
                  <LoadingButton
                    variant="contained"
                    size="small"
                    color="green"
                    sx={{
                      padding: '6px 15px',
                      fontSize: '14px',
                    }}
                    onClick={handleShowCodes}
                    loading={isInvitingEnterpriseBulk}
                  >
                    Generate a New Code
                  </LoadingButton>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      padding: '6px 15px',
                      fontSize: '14px',
                      minWidth: '100px',
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AddCompanyEnterpriseModal;
